import { Link } from "react-router-dom";
import {
    Eye,
    Compass,
    MapPinned,
    Gauge,
    Cpu,
    Radar,
    ScanLine,
    Route,
    ArrowUpRight,
    Quote,
} from "lucide-react";
import { useT } from "../i18n";

const MEDIA = {
    hero: "https://customer-assets.emergentagent.com/job_project-showcase-233/artifacts/xamjg6cl_picture%20%281%29.jpg",
};

const introIcons = [Eye, Compass, MapPinned, Gauge];
const methodIcons = [Compass, Cpu, ScanLine, Route];

export const Home = () => {
    const { t } = useT();

    const introCards = t("intro.cards");
    const methodCards = t("methodology.cards");
    const objectives = t("research.objectives");
    const journeySteps = t("journey.steps");
    const positioningStats = t("positioning.stats");
    const col1 = t("analysis.col1");
    const col2 = t("analysis.col2");
    const chartLegend = t("chart.legend");
    const marqueeTerms = t("marquee");

    return (
        <main
            data-testid="home-page"
            className="relative bg-white text-[#0E1A2B]"
        >
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7">
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8 animate-fade-up">
                            <span className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0]">
                                {t("hero.tagYear")}
                            </span>
                            <span className="font-mono text-xs uppercase tracking-[0.18em] text-[#595959]">
                                {t("hero.tagDomain")}
                            </span>
                        </div>

                        <h1
                            data-testid="hero-title"
                            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tight text-[#0033A0] animate-fade-up delay-1"
                        >
                            <span className="block">{t("hero.titleLine1")}</span>
                            <span className="block">
                                {t("hero.titleLine2")}
                                <span className="inline-block w-3 h-3 md:w-4 md:h-4 bg-[#FFC72C] ml-3 align-middle" />
                            </span>
                        </h1>

                        <p className="mt-6 font-display italic text-2xl md:text-3xl text-[#0033A0] max-w-xl leading-snug animate-fade-up delay-2">
                            {t("hero.tagline")}
                        </p>

                        <p className="mt-8 text-base md:text-lg leading-relaxed text-[#595959] max-w-xl animate-fade-up delay-3">
                            {t("hero.body")}
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-4 animate-fade-up delay-4">
                            <a
                                href="#journey"
                                data-testid="hero-cta-journey"
                                className="inline-flex items-center gap-2 bg-[#0033A0] text-white px-7 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[#002277] transition-colors"
                            >
                                {t("hero.ctaMission")}
                                <ArrowUpRight size={16} strokeWidth={1.5} />
                            </a>
                            <a
                                href="#methodology"
                                data-testid="hero-cta-methodology"
                                className="inline-flex items-center gap-2 bg-[#FFC72C] text-[#0033A0] px-7 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[#0033A0] hover:text-[#FFC72C] transition-colors"
                            >
                                {t("hero.ctaMethodology")}
                            </a>
                            <Link
                                to="/video"
                                data-testid="hero-cta-video"
                                className="inline-flex items-center gap-2 border border-[#0033A0] text-[#0033A0] px-7 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[#0033A0] hover:text-white transition-colors"
                            >
                                {t("hero.ctaVideo")}
                            </Link>
                        </div>
                    </div>

                    <div className="md:col-span-5 relative animate-fade-in delay-3">
                        <div className="relative aspect-[4/5] overflow-hidden border border-[#E5EAF2] bg-white">
                            <img
                                src={MEDIA.hero}
                                alt="The search and rescue robot in the field"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute top-3 left-3 right-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-white mix-blend-difference">
                                <span>{t("hero.unitLabel")}</span>
                                <span>{t("hero.statusLabel")}</span>
                            </div>
                            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-white mix-blend-difference">
                                <span>{t("hero.feedLabel")}</span>
                                <span className="flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 bg-[#FFC72C] rounded-full animate-pulse" />
                                    {t("hero.recLabel")}
                                </span>
                            </div>
                        </div>
                        <div className="absolute -bottom-6 -left-6 hidden md:block bg-[#FFC72C] border border-[#0033A0] p-5 max-w-[260px]">
                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0033A0] mb-2 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-[#0033A0] rounded-full animate-pulse" />
                                {t("hero.pillLabel")}
                            </p>
                            <p className="font-display text-base md:text-lg leading-snug text-[#0033A0]">
                                {t("hero.pillBody")}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Marquee */}
                <div className="border-y border-[#0033A0] bg-[#0033A0] overflow-hidden">
                    <div className="flex animate-marquee whitespace-nowrap py-4">
                        {Array.from({ length: 2 }).map((_, idx) => (
                            <div
                                key={idx}
                                className="flex items-center gap-12 px-6 font-mono text-xs uppercase tracking-[0.22em] text-white"
                            >
                                {marqueeTerms.map((term, ti) => (
                                    <span key={ti} className="flex items-center gap-12">
                                        <span>{term}</span>
                                        <span className="text-[#FFC72C]">/</span>
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* JOURNEY / NARRATIVE */}
            <section
                id="journey"
                data-testid="journey-section"
                className="relative bg-[#EEF3FB] border-b border-[#E5EAF2] scroll-mt-20"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
                        <div className="md:col-span-7">
                            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0] mb-4">
                                {t("journey.tag")}
                            </p>
                            <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] text-[#0033A0]">
                                {t("journey.heading")}
                            </h2>
                        </div>
                    </div>

                    {/* Narrative paragraph */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
                        <div className="md:col-span-1 hidden md:block">
                            <span className="inline-block w-10 h-10 bg-[#FFC72C] border-2 border-[#0033A0]" />
                        </div>
                        <div className="md:col-span-10">
                            <p className="font-display text-xl md:text-2xl leading-[1.55] text-[#0E1A2B]">
                                {t("journey.narrative")}
                            </p>
                        </div>
                    </div>

                    {/* Step grid — text only */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
                        {journeySteps.map((j, i) => (
                            <article
                                key={i}
                                data-testid={`journey-step-${i}`}
                                className="bg-white border border-[#0033A0]/15 p-6 hover:border-[#FFC72C] hover:shadow-[0_20px_40px_-20px_rgba(0,51,160,0.25)] transition-all duration-500 relative pt-10"
                            >
                                <div className="absolute -top-5 left-6 w-10 h-10 bg-[#FFC72C] border-2 border-[#0033A0] flex items-center justify-center font-mono text-sm text-[#0033A0] font-bold">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <h3 className="font-display text-xl leading-tight text-[#0033A0] mb-3">
                                    {j.title}
                                </h3>
                                <p className="text-sm leading-relaxed text-[#445268]">
                                    {j.body}
                                </p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* INTRODUCTION */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
                    <div className="md:col-span-4">
                        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0] mb-4">
                            {t("intro.tag")}
                        </p>
                        <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#0033A0]">
                            {t("intro.heading")}
                        </h2>
                    </div>
                    <div className="md:col-span-7 md:col-start-6">
                        <p className="text-lg leading-relaxed text-[#445268]">
                            {t("intro.body")}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5EAF2] border border-[#E5EAF2]">
                    {introCards.map((c, i) => {
                        const Icon = introIcons[i];
                        return (
                            <article
                                key={i}
                                data-testid={`intro-card-${i}`}
                                className="group bg-white p-8 md:p-10 hover:bg-[#FFC72C] transition-colors duration-500 relative"
                            >
                                <div className="flex items-center justify-between mb-10">
                                    <span className="font-mono text-xs text-[#0033A0]/70 group-hover:text-[#0033A0]">
                                        {c.label}
                                    </span>
                                    <Icon
                                        size={28}
                                        strokeWidth={1.25}
                                        className="text-[#0033A0]"
                                    />
                                </div>
                                <h3 className="font-display text-2xl leading-tight mb-3 text-[#0033A0]">
                                    {c.title}
                                </h3>
                                <p className="text-sm leading-relaxed text-[#445268] group-hover:text-[#0033A0]">
                                    {c.body}
                                </p>
                            </article>
                        );
                    })}
                </div>
            </section>

            {/* RESEARCH CONTEXT */}
            <section className="border-y border-[#E5EAF2] bg-[#EEF3FB]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-6">
                        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0] mb-6">
                            {t("research.tagQ")}
                        </p>
                        <Quote
                            size={36}
                            strokeWidth={1.25}
                            className="text-[#FFC72C] mb-4"
                        />
                        <h2 className="font-display italic text-3xl md:text-5xl leading-tight tracking-tight text-[#0033A0]">
                            {t("research.question")}
                        </h2>
                    </div>

                    <div className="md:col-span-5 md:col-start-8">
                        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0] mb-6">
                            {t("research.tagObj")}
                        </p>
                        <ol className="space-y-6">
                            {objectives.map((o, i) => (
                                <li
                                    key={i}
                                    className="grid grid-cols-[auto_1fr] gap-5"
                                >
                                    <span className="font-mono text-sm text-[#0033A0] bg-[#FFC72C] w-7 h-7 flex items-center justify-center font-bold">
                                        0{i + 1}
                                    </span>
                                    <p className="text-base leading-relaxed text-[#0E1A2B]">
                                        {o}
                                    </p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            {/* METHODOLOGY — text-only, no photo */}
            <section
                id="methodology"
                className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 scroll-mt-20"
            >
                <div className="mb-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0] mb-4">
                            {t("methodology.tag")}
                        </p>
                        <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] text-[#0033A0]">
                            {t("methodology.heading")}
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9">
                        <p className="text-sm leading-relaxed text-[#445268]">
                            {t("methodology.sub")}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {methodCards.map((m, i) => {
                        const Icon = methodIcons[i];
                        return (
                            <article
                                key={i}
                                data-testid={`method-card-${i}`}
                                className="bg-white border border-[#E5EAF2] p-8 hover:-translate-y-1 hover:border-[#FFC72C] transition-all duration-300 relative overflow-hidden"
                            >
                                <span className="absolute top-0 left-0 h-1 w-12 bg-[#FFC72C]" />
                                <Icon
                                    size={26}
                                    strokeWidth={1.25}
                                    className="text-[#0033A0] mb-8"
                                />
                                <h3 className="font-display text-xl mb-3 leading-tight text-[#0033A0]">
                                    {m.title}
                                </h3>
                                <p className="text-sm leading-relaxed text-[#445268]">
                                    {m.body}
                                </p>
                            </article>
                        );
                    })}
                </div>
            </section>

            {/* POSITIONING RELIABILITY — text only */}
            <section className="bg-[#FFC72C] text-[#0033A0] relative overflow-hidden border-y border-[#0033A0]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-7">
                        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0] mb-4">
                            {t("positioning.tag")}
                        </p>
                        <h2 className="font-display text-4xl md:text-6xl leading-[1.02] mb-10">
                            {t("positioning.heading")}
                        </h2>
                        <div className="space-y-6 max-w-2xl">
                            <div className="border-l-2 border-[#0033A0] pl-6 py-1">
                                <p className="font-display text-xl md:text-2xl mb-1">
                                    {t("positioning.hybridTitle")}
                                </p>
                                <p className="text-sm md:text-base leading-relaxed text-[#0033A0]/85">
                                    {t("positioning.hybridBody")}
                                </p>
                            </div>
                            <div className="border-l-2 border-[#0033A0] pl-6 py-1">
                                <p className="font-display text-xl md:text-2xl mb-1">
                                    {t("positioning.stabilityTitle")}
                                </p>
                                <p className="text-sm md:text-base leading-relaxed text-[#0033A0]/85">
                                    {t("positioning.stabilityBody")}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-4 md:col-start-9 flex flex-col gap-4 md:justify-center">
                        {positioningStats.map((s, i) => (
                            <div
                                key={i}
                                className="border-t-2 border-[#0033A0] pt-4"
                            >
                                <p className="font-display text-3xl md:text-4xl">
                                    {s.v}
                                </p>
                                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0033A0]/80 mt-1">
                                    {s.l}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CHART / Mission infographic */}
            <section
                id="chart"
                className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
                    <div className="md:col-span-7">
                        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0] mb-4">
                            {t("chart.tag")}
                        </p>
                        <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#0033A0]">
                            {t("chart.heading")}
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9">
                        <p className="text-sm leading-relaxed text-[#445268]">
                            {t("chart.sub")}
                        </p>
                    </div>
                </div>

                <div
                    data-testid="chart-placeholder"
                    className="relative border-2 border-[#0033A0]/15 bg-white p-4 md:p-8 overflow-hidden"
                >
                    <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#0033A0] flex items-center gap-2 z-10">
                        <span className="w-1.5 h-1.5 bg-[#FFC72C]" />
                        {t("chart.figLabel")}
                    </div>
                    <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#0033A0] z-10">
                        {t("chart.figRight")}
                    </div>
                    <img
                        src="https://customer-assets.emergentagent.com/job_project-showcase-233/artifacts/jni2j2d0_Ekran%20g%C3%B6r%C3%BCnt%C3%BCs%C3%BC%202026-05-12%20164310_imgupscaler.ai_Beta_2K.jpg"
                        alt={t("chart.imgAlt")}
                        className="w-full h-auto object-contain mx-auto max-w-3xl"
                        data-testid="chart-image"
                    />

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8 pt-6 border-t border-[#E5EAF2]">
                        {chartLegend.map((w, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0033A0] bg-[#FFC72C] w-7 h-7 flex items-center justify-center font-bold">
                                    {w.n}
                                </span>
                                <span className="text-xs text-[#445268]">
                                    {w.l}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ANALYSIS & DISCUSSION */}
            <section className="border-t border-[#E5EAF2] bg-[#EEF3FB]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
                    <div className="mb-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
                        <div className="md:col-span-8">
                            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0] mb-4">
                                {t("analysis.tag")}
                            </p>
                            <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] text-[#0033A0]">
                                {t("analysis.heading")}
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <div className="bg-white p-8 md:p-10 border border-[#0033A0]/15">
                            <div className="flex items-center gap-3 mb-6">
                                <Radar
                                    size={22}
                                    strokeWidth={1.25}
                                    className="text-[#0033A0]"
                                />
                                <h3 className="font-display text-2xl text-[#0033A0]">
                                    {t("analysis.col1Title")}
                                </h3>
                            </div>
                            <ul className="space-y-6">
                                {col1.map((it, i) => (
                                    <li
                                        key={i}
                                        className={
                                            i < col1.length - 1
                                                ? "pb-6 border-b border-[#E5EAF2]"
                                                : ""
                                        }
                                    >
                                        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0] mb-2">
                                            <span className="inline-block w-1.5 h-1.5 bg-[#FFC72C] mr-2 align-middle" />
                                            {it.t}
                                        </p>
                                        <p className="text-[#445268] leading-relaxed">
                                            {it.b}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-white p-8 md:p-10 border border-[#0033A0]/15">
                            <div className="flex items-center gap-3 mb-6">
                                <MapPinned
                                    size={22}
                                    strokeWidth={1.25}
                                    className="text-[#0033A0]"
                                />
                                <h3 className="font-display text-2xl text-[#0033A0]">
                                    {t("analysis.col2Title")}
                                </h3>
                            </div>
                            <ul className="space-y-6">
                                {col2.map((it, i) => (
                                    <li
                                        key={i}
                                        className={
                                            i < col2.length - 1
                                                ? "pb-6 border-b border-[#E5EAF2]"
                                                : ""
                                        }
                                    >
                                        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0] mb-2">
                                            <span className="inline-block w-1.5 h-1.5 bg-[#FFC72C] mr-2 align-middle" />
                                            {it.t}
                                        </p>
                                        <p className="text-[#445268] leading-relaxed">
                                            {it.b}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* RESULTS */}
            <section className="border-t border-[#E5EAF2] bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-8">
                        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#0033A0] mb-6">
                            {t("results.tag")}
                        </p>
                        <p className="font-display text-3xl md:text-5xl leading-[1.1] text-[#0033A0]">
                            {t("results.body1")}{" "}
                            <span className="italic relative">
                                {t("results.highlight")}
                                <span className="absolute left-0 right-0 -bottom-1 h-2 bg-[#FFC72C] -z-0" />
                            </span>{" "}
                            {t("results.body2")}
                        </p>
                    </div>
                    <div className="md:col-span-3 md:col-start-10 flex md:justify-end">
                        <Link
                            to="/gallery"
                            data-testid="results-cta-gallery"
                            className="inline-flex items-center gap-2 bg-[#0033A0] text-white px-7 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[#FFC72C] hover:text-[#0033A0] transition-colors"
                        >
                            {t("results.cta")}
                            <ArrowUpRight size={16} strokeWidth={1.5} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Home;
